import {
  DetailItem,
  TitlePageAsideLayout,
} from '@app/titles/pages/title-page/title-page-aside-layout';
import {PersonPoster} from '@app/people/person-poster/person-poster';
import {Trans} from '@common/i18n/trans';
import {FormattedDate} from '@common/i18n/formatted-date';
import React, {useEffect, useState} from 'react';
import {PersonAge} from '@app/people/person-age';
import {GetPersonResponse} from '@app/people/requests/use-person';
import {ShareMenuTrigger} from '@app/sharing/share-menu-trigger';
import {Button} from '@common/ui/buttons/button';
import {ShareIcon} from '@common/icons/material/Share';
import {Person} from '@app/titles/models/person';
import {getPersonLink} from '@app/people/person-link';
import {IconButton} from '@common/ui/buttons/icon-button';
import {Link} from 'react-router-dom';
import {EditIcon} from '@common/icons/material/Edit';
import {useAuth} from '@common/auth/use-auth';
import {Swiper, SwiperSlide} from 'swiper/react';
import {ArrowCircleLeft, ArrowCircleRight} from 'iconsax-react';
import {Autoplay, Navigation} from 'swiper/modules';
import {useSelectedLocale} from '@common/i18n/selected-locale';

interface Props {
  data: GetPersonResponse;
}
export function PersonPageAside({
  data: {person, images, total_credits_count},
}: Props) {
  const [key, setKey] = useState<number>(1);

  const {hasPermission} = useAuth();

  const {locale} = useSelectedLocale();

  const age = (
    <Trans
      message=":count years old"
      values={{count: <PersonAge person={person} />}}
    />
  );

  useEffect(() => {
    setKey(prev => prev + 1);
  }, [locale]);

  return (
    <TitlePageAsideLayout
      className="max-md:flex"
      poster={
        <div>
          <div className="relative">
            <Swiper
              key={key}
              dir="rtl"
              modules={[Navigation, Autoplay]}
              loop
              autoplay
              initialSlide={Math.ceil(Math.random() * images.length)}
              navigation={{
                prevEl: '.swiper-banner-button-prev',
                nextEl: '.swiper-banner-button-next',
              }}
              className="mySwiper w-140 md:w-full"
            >
              {images?.length === 0 ? (
                <PersonPoster
                  person={person}
                  size="w-140 md:w-full"
                  srcSize="lg"
                />
              ) : (
                images?.map(image => (
                  <SwiperSlide key={image.id}>
                    <img
                      className="min-w-full rounded-sm"
                      src={image.url}
                      alt=""
                    />
                  </SwiperSlide>
                ))
              )}
              <ArrowCircleRight
                id="swiper-banner-button-prev"
                className="swiper-banner-button-prev absolute top-1/2 z-20 cursor-pointer select-none"
              />
              <ArrowCircleLeft
                id="swiper-banner-button-next"
                className="swiper-banner-button-next absolute left-30 top-1/2 z-20 cursor-pointer select-none"
              />
            </Swiper>
            {hasPermission('titles.update') && (
              <IconButton
                elementType={Link}
                to={`/admin/people/${person.id}/edit`}
                className="absolute bottom-6 right-4 z-20"
                color="white"
              >
                <EditIcon />
              </IconButton>
            )}
          </div>
          {/* <ShareButton person={person} /> */}
        </div>
      }
    >
      <dl className="mt-12 md:mt-24">
        {person.known_for && (
          <DetailItem label={<Trans message="Known for" />}>
            <Trans message={person.known_for} />
          </DetailItem>
        )}
        {person.gender && (
          <DetailItem label={<Trans message="Gender" />}>
            <span className="capitalize">
              <Trans message={person.gender} />
            </span>
          </DetailItem>
        )}
        {total_credits_count ? (
          <DetailItem label={<Trans message="Known credits" />}>
            {total_credits_count}
          </DetailItem>
        ) : null}
        {person.birth_date ? (
          <DetailItem label={<Trans message="Born" />}>
            {person?.birth_date?.includes('Z') ? (
              <FormattedDate date={person.birth_date} />
            ) : (
              person.birth_date.split(' ')[0].replaceAll('-', '/')
            )}
            &nbsp;
            {!person.death_date && age}
          </DetailItem>
        ) : null}
        {person.birth_place ? (
          <DetailItem label={<Trans message="Birthplace" />}>
            {locale.language === 'fa' && person.persian_birth_place
              ? person.persian_birth_place
              : person.birth_place}
          </DetailItem>
        ) : null}
        {person.death_date ? (
          <DetailItem label={<Trans message="Died" />}>
            {person?.death_date?.includes('Z') ? (
              <FormattedDate date={person.death_date} />
            ) : (
              person.death_date.split(' ')[0].replaceAll('-', '/')
            )}
            &nbsp;
            {age && (
              <>
                (<PersonAge person={person} /> <Trans message="years old" />)
              </>
            )}
          </DetailItem>
        ) : null}
      </dl>
    </TitlePageAsideLayout>
  );
}

interface ShareButtonProps {
  person: Person;
}
function ShareButton({person}: ShareButtonProps) {
  const link = getPersonLink(person, {absolute: true});
  return (
    <ShareMenuTrigger link={link}>
      <Button
        variant="outline"
        color="primary"
        startIcon={<ShareIcon />}
        className="mt-14 md:min-h-40 md:w-full"
      >
        <Trans message="Share" />
      </Button>
    </ShareMenuTrigger>
  );
}
